'use client';

import { useState, useEffect, useRef } from 'react';
import { AIUpload } from '@prisma/client';
import { LoadingSpinner } from '@/components/loading-indicator';
import { useRouter } from 'next/navigation';
import GenericTable from '@/components/ui/dynamic-tables'; // Assuming the generic table is in this path
import { Session } from 'next-auth';

type FieldType = 'string' | 'number' | 'date' | 'currency' | 'image';

interface FieldConfig {
	index: number;
	label: string;
	type: FieldType;
	transform?: (value: any) => string | JSX.Element;
}

export default function AIUploadsClient({
	show_loading,
	session
}: {
	show_loading?: boolean;
	session: Session | null;
}) {
	const [uploads, setUploads] = useState<AIUpload[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const siteIdRef = useRef<string | null>(null);

	const router = useRouter();
	const itemsPerPage = 10;

	// Define the field map within the component
	const fieldMap: { [key: string]: FieldConfig } = {
		prompt: {
			index: 0,
			label: 'Prompt',
			type: 'string',
			transform: (value: string) => <p>{value}</p>
		},
		createdAt: {
			index: 1,
			label: 'Created At',
			type: 'string',
			transform: (value: string) => <p>{value}</p>
		}
	};

	useEffect(() => {
		if (!session || !session?.siteId) {
			return;
		}

		siteIdRef.current = session.siteId;
		fetchUploads(session.siteId);
	}, [session]);

	const fetchUploads = async (siteId: string) => {
		try {
			const response = await fetch(`/api/sites/${siteId}/aiuploads`);
			if (!response.ok) {
				throw new Error('Failed to fetch uploads');
			}
			const data = await response.json();
			setUploads(data);
			setLoading(false);
		} catch (err: any) {
			setError(err.message);
			setLoading(false);
		}
	};

	const handleRowClick = (uploadId: string) => {
		router.push(`/ai-uploads/${uploadId}`);
	};

	if (show_loading) {
		if (loading) return <LoadingSpinner />;
	} else {
		if (loading) return null;
	}

	if (error) return null;
	if (!session?.siteId) return <div>Access Denied</div>;
	if (uploads.length === 0) return null;

	return (
		<GenericTable
			title="Previous Lists"
			description="List of previously uploaded files."
			data={uploads}
			fieldMap={fieldMap}
			maxColumnsMobile={3}
			itemsPerPage={itemsPerPage}
			handleRowClick={(id) => handleRowClick(id)}
			showUnmapped={false}
			disableVerticalScroll={true}
		/>
	);
}
